import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowRight, Download, Copy, CheckCircle2, AlertTriangle, Loader2, Calendar, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { CALENDLY_URL } from '../../lib/siteConfig';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DIM_ORDER = ['composure', 'confidence', 'awareness', 'focus', 'resilience', 'preparation'];
const DIM_LABEL = {
  composure: 'Composure',
  confidence: 'Confidence',
  awareness: 'Awareness',
  focus: 'Focus',
  resilience: 'Resilience',
  preparation: 'Preparation',
};

function Narrative({ text }) {
  // The narrative may contain **bold** markers from the engine. Render them simply.
  const parts = (text || '').split(/(\*\*[^*]+\*\*)/g);
  return (
    <p className="text-sm text-foreground/75 leading-relaxed">
      {parts.map((p, i) =>
        p.startsWith('**') && p.endsWith('**') ? (
          <b key={i} className="text-[#004C54]">{p.slice(2, -2)}</b>
        ) : (
          <React.Fragment key={i}>{p}</React.Fragment>
        ),
      )}
    </p>
  );
}

export default function AssessmentResults() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = 'Your Apex Six IDP';
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/assessment/results/${encodeURIComponent(token)}`);
        if (!r.ok) {
          const b = await r.json().catch(() => ({}));
          if (!cancelled) setErr(b.detail || `Could not load results (${r.status}).`);
          return;
        }
        const d = await r.json();
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setErr('Network error. Please refresh.');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-5" data-testid="results-error">
        <div className="max-w-md w-full bg-white border border-[#E5E7E7] rounded-2xl p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
          <h1 className="font-heading text-xl font-bold tracking-tight mt-4">Can&rsquo;t load your results</h1>
          <p className="text-sm text-foreground/65 mt-2">{err}</p>
          <a href="/assessment" className="inline-block mt-5 text-[#004C54] underline underline-offset-4">Back to /assessment</a>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center" data-testid="results-loading">
        <Loader2 className="h-8 w-8 text-[#004C54] animate-spin" />
      </div>
    );
  }

  const s = data.scores || {};
  const dimScores = s.dimension_scores || {};
  const topPriority = (s.priorities || [])[0];
  const secondaryPriority = (s.priorities || [])[1];
  const strengths = s.strengths || [];
  const actionSteps = s.action_steps || [];
  const band = s.band || {};

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(data.discount_code || '');
      setCopied(true);
      toast.success('Code copied — paste it in the Calendly booking notes within 7 days.');
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error('Could not copy. Tap the code to select it manually.');
    }
  };

  const pdfUrl = `${BACKEND}/api/assessment/pdf/${encodeURIComponent(token)}`;

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#0a0d0e]" data-testid="assessment-results">
      {/* Header */}
      <header className="border-b border-black/5 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-[#004C54] text-white flex items-center justify-center font-bold">A</div>
            <span className="font-semibold tracking-tight text-sm">Apex Athletic Mind</span>
          </div>
          <Badge variant="outline" className="border-[#4FBCC7]/40 text-[#004C54] uppercase tracking-[0.2em] text-[10px] font-bold">
            Individual Development Plan
          </Badge>
        </div>
      </header>

      {/* Hero block */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16 pb-8">
        <div className="text-[11px] tracking-[0.2em] font-bold text-[#004C54] font-mono uppercase">Your Apex Six IDP</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-2" data-testid="results-headline">
          {data.name ? `${data.name}, here's your plan.` : "Here's your plan."}
        </h1>
        <p className="mt-4 text-lg text-foreground/65 max-w-[60ch]" data-testid="results-band-summary">{band.summary}</p>
      </section>

      {/* Snapshot */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-8">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-5">
          <Card className="border-[#E5E7E7] overflow-hidden" data-testid="results-overall-card">
            <div className="bg-[#004C54] px-6 py-5 text-white">
              <div className="text-[10px] tracking-[0.25em] font-bold font-mono uppercase opacity-80">Overall</div>
              <div className="flex items-baseline gap-3 mt-1">
                <span className="font-heading text-6xl font-bold leading-none tabular-nums">{Math.round(s.overall_score || 0)}</span>
                <span className="opacity-80">/ 100</span>
              </div>
              <div className="mt-2 text-sm font-semibold">{band.label}</div>
            </div>
            <CardContent className="p-6 space-y-3">
              {DIM_ORDER.map((d) => {
                const v = Math.round(dimScores[d] || 0);
                return (
                  <div key={d} className="flex items-center gap-3" data-testid={`results-dim-${d}`}>
                    <div className="w-24 text-xs font-semibold text-foreground/70">{DIM_LABEL[d]}</div>
                    <div className="flex-1 h-2 rounded-full bg-[#F0F4F4] overflow-hidden">
                      <div
                        className={`h-full rounded-full ${v >= 70 ? 'bg-[#4FBCC7]' : 'bg-[#004C54]'}`}
                        style={{ width: `${Math.max(2, v)}%` }}
                      />
                    </div>
                    <div className="w-8 text-right text-xs font-semibold tabular-nums">{v}</div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Primary action */}
          {topPriority && (
            <Card className="border-[#004C54]/30 shadow-[0_24px_64px_rgba(0,76,84,0.10)]" data-testid="results-primary-action">
              <CardContent className="p-7">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] tracking-[0.25em] font-bold font-mono uppercase text-[#004C54]">Primary Action — Start Here</div>
                  <Sparkles className="h-4 w-4 text-[#4FBCC7]" />
                </div>
                <h2 className="font-heading text-3xl font-bold tracking-tight mt-3" data-testid="results-primary-label">{topPriority.label}</h2>
                <div className="text-sm text-foreground/60 mt-1">
                  Score: <b>{Math.round(topPriority.score)}/100</b> · Prescribed framework: <b>{topPriority.framework}</b>
                </div>
                <div className="mt-4">
                  <Narrative text={topPriority.narrative} />
                </div>
                <div className="mt-6">
                  <div className="text-[10px] tracking-[0.2em] font-bold text-[#004C54] font-mono uppercase">Your 30-day action steps</div>
                  <ol className="mt-3 space-y-3">
                    {actionSteps.map((step, i) => (
                      <li key={step} className="flex gap-3" data-testid={`results-action-${i}`}>
                        <div className="shrink-0 w-7 h-7 rounded-full bg-[#004C54]/10 text-[#004C54] flex items-center justify-center text-xs font-bold">{i + 1}</div>
                        <p className="text-sm text-foreground/80 leading-relaxed pt-0.5">{step}</p>
                      </li>
                    ))}
                  </ol>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Strengths + secondary */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-8 grid md:grid-cols-2 gap-5">
        <Card className="border-[#E5E7E7]" data-testid="results-strengths-card">
          <CardContent className="p-7">
            <div className="text-[10px] tracking-[0.25em] font-bold font-mono uppercase text-[#004C54]">What to protect — Your strengths</div>
            <div className="mt-4 space-y-5">
              {strengths.map((st) => (
                <div key={st.key} data-testid={`results-strength-${st.key}`}>
                  <div className="flex items-baseline justify-between">
                    <h3 className="font-heading text-lg font-semibold tracking-tight">{st.label}</h3>
                    <span className="text-sm font-mono font-semibold text-[#004C54]">{Math.round(st.score)}/100</span>
                  </div>
                  <p className="text-sm text-foreground/65 mt-1.5 leading-relaxed">{st.narrative}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        {secondaryPriority && (
          <Card className="border-[#E5E7E7]" data-testid="results-secondary-card">
            <CardContent className="p-7">
              <div className="text-[10px] tracking-[0.25em] font-bold font-mono uppercase text-[#004C54]">Secondary Priority</div>
              <h3 className="font-heading text-xl font-semibold tracking-tight mt-3">{secondaryPriority.label}</h3>
              <div className="text-sm text-foreground/60 mt-1">
                Score <b>{Math.round(secondaryPriority.score)}/100</b> · Prescribed: <b>{secondaryPriority.framework}</b>
              </div>
              <div className="mt-3">
                <Narrative text={secondaryPriority.narrative} />
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Discovery Call CTA */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
        <Card className="overflow-hidden border-none bg-[#004C54] text-white shadow-[0_24px_64px_rgba(0,76,84,0.20)]" data-testid="results-discovery-card">
          <CardContent className="p-7 sm:p-10">
            <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 items-center">
              <div>
                <div className="text-[10px] tracking-[0.25em] font-bold font-mono uppercase text-white/70">Next step — 25% off your first 1:1 session</div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-3">Want me to walk you through this in person?</h2>
                <p className="mt-3 text-white/85 max-w-[55ch]">
                  Book a 30-minute Discovery Call within the next 7 days, paste your code into the booking notes, and you&rsquo;ll get
                  <b> 25% off your first 1:1 session</b>. We&rsquo;ll line up exactly how to install your Primary Action over the next four weeks.
                </p>
              </div>
              <div className="bg-white/10 border border-white/20 rounded-2xl p-5" data-testid="results-discount-block">
                <div className="text-[10px] tracking-[0.25em] font-bold font-mono uppercase text-white/70">25% off your first 1:1 session</div>
                <div className="mt-2 flex items-center gap-3">
                  <code
                    className="font-mono text-xl sm:text-2xl font-bold tracking-tight select-all"
                    data-testid="results-discount-code"
                  >{data.discount_code}</code>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={copyCode}
                    className="bg-white text-[#004C54] hover:bg-white/90"
                    data-testid="results-copy-code"
                  >
                    {copied ? <CheckCircle2 className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                {data.code_expires_at_human && (
                  <p className="text-xs text-white/85 mt-2" data-testid="results-discount-expiry">
                    <b>Valid through {data.code_expires_at_human}.</b> Paste in the Calendly notes when you book.
                  </p>
                )}
                {!data.code_expires_at_human && (
                  <p className="text-xs text-white/70 mt-2">Valid for 7 days. Paste in the booking notes when you schedule.</p>
                )}
                <Button
                  asChild
                  size="lg"
                  className="mt-4 w-full bg-[#4FBCC7] hover:bg-[#3aa8b4] text-[#003a40] font-semibold"
                  data-testid="results-calendly-cta"
                >
                  <a href={data.calendly_url || CALENDLY_URL} target="_blank" rel="noreferrer">
                    <Calendar className="h-4 w-4 mr-2" /> Book my Discovery Call <ArrowRight className="h-4 w-4 ml-2" />
                  </a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* PDF download */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
        <Card className="border-[#E5E7E7]" data-testid="results-pdf-card">
          <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="text-[10px] tracking-[0.25em] font-bold font-mono uppercase text-[#004C54]">Take it with you</div>
              <h3 className="font-heading text-lg font-semibold tracking-tight mt-1">Your printable IDP PDF</h3>
              <p className="text-sm text-foreground/60 mt-1">{data.pdf_sent ? `We emailed it to ${data.email}. ` : ''}You can also download it directly.</p>
            </div>
            <Button asChild className="bg-[#0a0d0e] hover:bg-black" data-testid="results-pdf-download">
              <a href={pdfUrl} target="_blank" rel="noreferrer">
                <Download className="h-4 w-4 mr-2" /> Download PDF
              </a>
            </Button>
          </CardContent>
        </Card>
      </section>

      {/*
        Workshop + Locker Room secondary next-steps (Phase 21.3)
        After the IDP buyer has their Primary Action + Discovery Call code,
        surface two complementary paths:
          1) Bring it to their team / school via a 60-90 min workshop. Scoped on a Planning Call.
          2) Drop into The Locker Room — playlists + Apex Six notecards.
        Discovery Call gets the primary 25% off slot above; this block
        captures buyers ready for bigger commitments or free engagement.
      */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-8" data-testid="results-secondary-ctas">
        <div className="grid sm:grid-cols-2 gap-4">
          <Card className="border-[#E5E7E7] bg-gradient-to-br from-white to-[#F6F7F8]" data-testid="results-workshop-card">
            <CardContent className="p-6">
              <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] font-bold font-mono uppercase text-[#004C54] mb-2">
                <Sparkles className="h-3 w-3" /> Team Workshop · Scoped on a Planning Call.
              </div>
              <h3 className="font-heading text-xl font-bold tracking-tight mb-1.5">
                Bring the Apex Six to your team
              </h3>
              <p className="text-sm text-foreground/70 leading-snug mb-4 max-w-[40ch]">
                60–90 min in-person or virtual session for your roster. Composure, confidence, and pressure-response drills your players will use in the next game.
              </p>
              <Button
                asChild
                size="default"
                variant="outline"
                className="border-[#004C54] text-[#004C54] hover:bg-[#004C54]/10 font-semibold"
                data-testid="results-workshop-cta"
              >
                <a
                  href={data.calendly_url || CALENDLY_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    try {
                      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                        window.gtag('event', 'results_cta_click', { label: 'Workshop' });
                      }
                    } catch {
                      /* analytics best-effort */
                    }
                  }}
                >
                  <Calendar className="h-4 w-4 mr-2" /> Inquire about a Workshop
                </a>
              </Button>
            </CardContent>
          </Card>

          <Card className="border-none bg-[#0a0d0e] text-white" data-testid="results-locker-card">
            <CardContent className="p-6">
              <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] font-bold font-mono uppercase text-[#4FBCC7] mb-2">
                <Sparkles className="h-3 w-3" /> Free with your IDP
              </div>
              <h3 className="font-heading text-xl font-bold tracking-tight mb-1.5">
                Drop into The Locker Room
              </h3>
              <p className="text-sm text-white/65 leading-snug mb-4 max-w-[40ch]">
                Pre-game playlists, the Apex Six notecards, and the locker room your IDP just told you to build. Free to enter.
              </p>
              <Button
                asChild
                size="default"
                className="bg-[#4FBCC7] hover:bg-[#3aa8b4] text-[#003a40] font-semibold"
                data-testid="results-locker-cta"
              >
                <a
                  href="/locker-room"
                  onClick={() => {
                    try {
                      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                        window.gtag('event', 'results_cta_click', { label: 'Locker Room' });
                      }
                    } catch {
                      /* analytics best-effort */
                    }
                  }}
                >
                  Open The Locker Room <ArrowRight className="h-4 w-4 ml-2" />
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <footer className="border-t border-black/5 mt-8">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 text-xs text-foreground/55 flex flex-wrap items-center justify-between gap-3">
          <div>© Apex Athletic Mind Consulting · IDP report</div>
          <a href="/" className="hover:text-foreground/80">apexathleticmind.com →</a>
        </div>
      </footer>
    </div>
  );
}
